import { useEffect, useState } from 'react';
import { DeliveryMethod, DeliveryTypes } from '~/features/checkout/models/checkout';
import { ShippingMethod } from '~/lib/data-contract/salesforce';
import { DeliveryAddressList } from '../../../DeliveryAddressList';
import { DeliveryAddressSummary } from '../../../DeliveryAddressSummery/DeliveryAddressSummary';
import {
    StyledDeliveryAddressList,
    StyledDeliveryAddressWrapper,
    StyledDeliveryCloseIcon,
    StyledDeliveryCloseWrapper,
    StyledSubTitle,
    StyledTitle,
    StyledDeliveryBackIcon,
} from '../../styled';
import { useCheckout } from '~/features/checkout/hooks';
import { useTranslation } from '~/shared/utils/translation';

type Props = {
    optionList?: ShippingMethod[];
    setCarrier: (option: ShippingMethod, method?: DeliveryMethod) => void;
    selectedCarrier?: ShippingMethod;
    handleCloseDrawer: () => void;
};

export const DeliveryContentAddress = ({
    optionList,
    setCarrier,
    selectedCarrier,
    handleCloseDrawer,
}: Props) => {
    const { setShippingAddressInformation } = useCheckout();
    const { translate } = useTranslation();
    const [isDeliveryAddressSummaryHidden, setIsDeliveryAddressSummaryHidden] = useState(false);

    const doesDeliveryTypeAndShippingTypeMatch =
        selectedCarrier?.c_shippingType === DeliveryTypes.addressDelivery;

    const handleSaveAddressInformation = () => {
        handleCloseDrawer();
        setShippingAddressInformation();
        setIsDeliveryAddressSummaryHidden(false);
    };

    useEffect(() => {
        if (optionList?.length === 1) {
            const [option] = optionList ?? [];
            if (option) {
                setCarrier(option, 'addressDelivery');
            }
        }
    }, [optionList]);

    useEffect(() => {
        if (selectedCarrier) {
            if (doesDeliveryTypeAndShippingTypeMatch) {
                setCarrier(selectedCarrier);
            }
        }
    }, []);

    const selectCarrier = (carrier: ShippingMethod) => {
        setCarrier(carrier);
        setIsDeliveryAddressSummaryHidden(true);
    };

    return (
        <StyledDeliveryAddressWrapper>
            <StyledDeliveryCloseWrapper>
                {isDeliveryAddressSummaryHidden && (
                    <StyledDeliveryBackIcon
                        aria-label="Gå tilbage til leveringsmuligheder"
                        tabIndex={0}
                        style={{ marginRight: 'auto' }}
                        onClick={() => setIsDeliveryAddressSummaryHidden(false)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setIsDeliveryAddressSummaryHidden(false);
                            }
                        }}
                    />
                )}

                <StyledDeliveryCloseIcon
                    tabIndex={0}
                    aria-label="Luk side med hjemmeleveringsmuligheder"
                    data-testid="close-delivery-icon"
                    onClick={handleCloseDrawer}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCloseDrawer();
                        }
                    }}
                />
            </StyledDeliveryCloseWrapper>

            <StyledTitle>
                {translate(
                    !isDeliveryAddressSummaryHidden
                        ? 'checkout.delivery.chooseDeliveryMethod'
                        : 'checkout.checkout.delivery.chooseAddress',
                )}
            </StyledTitle>

            <StyledSubTitle>
                {translate('checkout.delivery.choosePreferedDeliveryMethod')}
            </StyledSubTitle>
            {doesDeliveryTypeAndShippingTypeMatch && isDeliveryAddressSummaryHidden && (
                <DeliveryAddressSummary selectAddress={handleSaveAddressInformation} />
            )}

            {!isDeliveryAddressSummaryHidden && (
                <StyledDeliveryAddressList>
                    <DeliveryAddressList optionList={optionList} setCarrier={selectCarrier} />
                </StyledDeliveryAddressList>
            )}
        </StyledDeliveryAddressWrapper>
    );
};
